import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  EditableCollectionBinding,
  EditableToken,
  useCollectionBinding,
  useContentToken,
  useProperty,
  usePropertyField,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

interface Amenity {
  title?: string | null;
  description?: string | null;
  group?: string | null;
  amenity_icon?: string | null;
  amenity_image?: string | null;
  amenity_image_alt_text?: string | null;
  is_featured?: string | null;
}

interface GalleryPhoto {
  image?: string | null;
  original_url?: string | null;
  title?: string | null;
  description?: string | null;
  alt_text?: string | null;
}

const SLIDE_INTERVAL = 5000;
const PAGE_SIZE = 10;

const COMMUNITY_KEYS = ['community', 'property', 'exterior', 'common area', 'site amenity'];
const APARTMENT_KEYS = ['apartment', 'unit', 'interior', 'in home', 'home feature'];
const COMMUNITY_PHOTO_KEYS = ['community', 'amenity', 'pool', 'clubhouse', 'fitness', 'gym', 'courtyard', 'lounge'];
const APARTMENT_PHOTO_KEYS = ['apartment', 'unit', 'interior', 'home', 'kitchen', 'bedroom', 'bath'];

function parseAmenityList(raw: string | null | undefined): Amenity[] {
  if (!raw) return [];
  return raw
    .split(/[\n;,•|]+/)
    .map((entry) => entry.replace(/^[-–—*]\s*/, '').trim())
    .filter(Boolean)
    .map((title) => ({ title }));
}

function normalize(value: string | null | undefined): string {
  return (value ?? '').toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
}

function matchesKeys(value: string | null | undefined, keys: string[]): boolean {
  const normalized = normalize(value);
  return !!normalized && keys.some((key) => normalized.includes(key));
}

function isApartmentGroup(group: string | null | undefined): boolean {
  const normalized = normalize(group);
  return matchesKeys(normalized, APARTMENT_KEYS) && !normalized.includes('community');
}

function isCommunityGroup(group: string | null | undefined): boolean {
  return matchesKeys(group, COMMUNITY_KEYS);
}

function photoHaystack(photo: GalleryPhoto): string {
  return `${photo.title ?? ''} ${photo.alt_text ?? ''} ${photo.description ?? ''}`;
}

function photosMatching(photos: GalleryPhoto[], keywords: string[]): GalleryPhoto[] {
  return photos.filter((photo) => matchesKeys(photoHaystack(photo), keywords));
}

function photosFromAmenities(items: Amenity[]): GalleryPhoto[] {
  return items
    .filter((item) => item.amenity_image)
    .map((item) => ({
      image: item.amenity_image,
      alt_text: item.amenity_image_alt_text,
      title: item.title,
    }));
}

function photoId(photo: GalleryPhoto): string {
  return photo.image || photo.original_url || photo.title || '';
}

function classifyAmenities(items: Amenity[]): {
  community: Amenity[];
  apartment: Amenity[];
  other: Amenity[];
} {
  const community: Amenity[] = [];
  const apartment: Amenity[] = [];
  const other: Amenity[] = [];

  for (const item of items) {
    if (isApartmentGroup(item.group)) apartment.push(item);
    else if (isCommunityGroup(item.group)) community.push(item);
    else other.push(item);
  }

  return { community, apartment, other };
}

function resolveAmenityColumns(
  items: Amenity[],
  communityList: Amenity[],
  apartmentList: Amenity[],
): { community: Amenity[]; apartment: Amenity[] } {
  const { community, apartment, other } = classifyAmenities(items);

  return {
    community: [
      ...(community.length > 0 ? community : communityList),
      ...other,
    ],
    apartment: apartment.length > 0 ? apartment : apartmentList,
  };
}

function resolveColumnPhotos(
  bound: GalleryPhoto[],
  allPhotos: GalleryPhoto[],
  keywords: string[],
  items: Amenity[],
  usedIds: Set<string>,
  fallbackToUnusedGallery: boolean,
): GalleryPhoto[] {
  const usableBound = bound.filter((photo) => photo.image || photo.original_url);
  if (usableBound.length > 0) return usableBound;

  const matched = photosMatching(allPhotos, keywords).filter((photo) => !usedIds.has(photoId(photo)));
  if (matched.length > 0) return matched;

  const fromItems = photosFromAmenities(items);
  if (fromItems.length > 0) return fromItems;

  if (!fallbackToUnusedGallery) return [];

  return allPhotos
    .filter((photo) => (photo.image || photo.original_url) && !usedIds.has(photoId(photo)))
    .slice(0, 8);
}

interface PhotoSliderProps {
  photos: GalleryPhoto[];
  heading: string;
}

function PhotoSlider({ photos, heading }: PhotoSliderProps) {
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const visiblePhotos = photos.filter((photo) => photo.image || photo.original_url);
  const count = visiblePhotos.length;

  const advance = useCallback((dir: 1 | -1) => {
    setCurrent((prev) => (prev + dir + count) % count);
  }, [count]);

  useEffect(() => {
    if (count <= 1 || paused) return;
    timerRef.current = setInterval(() => advance(1), SLIDE_INTERVAL);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [count, paused, advance]);

  if (count === 0) return null;

  return (
    <div
      className="amenities-slider"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {visiblePhotos.map((photo, i) => (
        <VibesitesImage
          key={photo.image || photo.original_url || i}
          url={(photo.image || photo.original_url)!}
          alt={photo.alt_text || photo.title || heading}
          className={`amenities-slider-img${i === current ? ' amenities-slider-img--active' : ''}`}
          sizes="(min-width: 768px) 520px, 100vw"
          priority={i === 0}
        />
      ))}

      {count > 1 && (
        <>
          <button
            className="amenities-slider-arrow amenities-slider-arrow--prev"
            onClick={() => advance(-1)}
            aria-label="Previous photo"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
          </button>
          <button
            className="amenities-slider-arrow amenities-slider-arrow--next"
            onClick={() => advance(1)}
            aria-label="Next photo"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
          </button>

          <div className="amenities-slider-dots">
            {visiblePhotos.map((_, i) => (
              <button
                key={i}
                className={`amenities-slider-dot${i === current ? ' amenities-slider-dot--active' : ''}`}
                onClick={() => setCurrent(i)}
                aria-label={`Photo ${i + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

interface AmenityColumnProps {
  headingNode: ReactNode;
  headingText: string;
  items: Amenity[];
  photos: GalleryPhoto[];
}

function AmenityColumn({
  headingNode,
  headingText,
  items,
  photos,
}: AmenityColumnProps) {
  const colRef = useScrollReveal<HTMLDivElement>();
  const listRef = useRef<HTMLUListElement>(null);
  const [page, setPage] = useState(0);
  const [transitioning, setTransitioning] = useState(false);

  const totalPages = Math.ceil(items.length / PAGE_SIZE);
  const pageItems = items.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const hasPagination = totalPages > 1;

  const goToPage = useCallback((next: number) => {
    if (next === page || transitioning) return;
    setTransitioning(true);
    setTimeout(() => {
      setPage(next);
      setTimeout(() => setTransitioning(false), 30);
    }, 250);
  }, [page, transitioning]);

  return (
    <div ref={colRef} className="reveal-fade-up amenities-col">
      {photos.length > 0 ? <PhotoSlider photos={photos} heading={headingText} /> : null}

      {headingNode}

      <ul
        ref={listRef}
        className={`amenities-col-list amenities-page-transition${transitioning ? ' amenities-page-out' : ''}`}
      >
        {pageItems.map((item, i) => (
          <li key={`${page}-${item.title || i}`} className="amenities-col-item">
            <span className="amenities-col-bullet" aria-hidden="true" />
            <span>{item.title || ''}</span>
          </li>
        ))}
      </ul>

      {hasPagination && (
        <div className="amenities-pagination">
          <button
            onClick={() => goToPage(page - 1)}
            disabled={page === 0 || transitioning}
            className="amenities-page-btn"
            aria-label="Previous page"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>

          <div className="amenities-page-dots">
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i}
                onClick={() => goToPage(i)}
                disabled={transitioning}
                className={`amenities-page-dot${i === page ? ' amenities-page-dot--active' : ''}`}
                aria-label={`Page ${i + 1}`}
                aria-current={i === page ? 'page' : undefined}
              />
            ))}
          </div>

          <button
            onClick={() => goToPage(page + 1)}
            disabled={page === totalPages - 1 || transitioning}
            className="amenities-page-btn"
            aria-label="Next page"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
}

export function AmenitiesList() {
  const communityHeading = useContentToken('amenitieslist.communityHeading');
  const apartmentHeading = useContentToken('amenitieslist.apartmentHeading');

  const communityBound = useCollectionBinding<Amenity>('amenitieslist.communityItems', {
    collection: 'amenities',
    filter: { group: 'Community' },
  });
  const apartmentBound = useCollectionBinding<Amenity>('amenitieslist.apartmentItems', {
    collection: 'amenities',
    filter: { group: 'Apartment' },
  });
  const communityPhotosBound = useCollectionBinding<GalleryPhoto>('amenitieslist.communityPhotos', {
    collection: 'photo_gallery',
    filter: { title: 'Community' },
    defaultLimit: 8,
  });
  const apartmentPhotosBound = useCollectionBinding<GalleryPhoto>('amenitieslist.apartmentPhotos', {
    collection: 'photo_gallery',
    filter: { title: 'Apartment' },
    defaultLimit: 8,
  });

  const property = useProperty();
  const communityListRaw = usePropertyField('property_community_amenities_list');
  const apartmentListRaw = usePropertyField('property_apartment_features_list');

  const { communityItems, apartmentItems, communityPhotos, apartmentPhotos } = useMemo(() => {
    const allAmenities = (property.amenities ?? []).filter((item) => item.title || item.description);
    const allPhotos = (property.photo_gallery ?? []) as GalleryPhoto[];
    const resolved = resolveAmenityColumns(
      allAmenities,
      parseAmenityList(communityListRaw),
      parseAmenityList(apartmentListRaw),
    );

    const community = communityBound.length > 0 ? communityBound : resolved.community;
    const apartment = apartmentBound.length > 0 ? apartmentBound : resolved.apartment;

    const usedPhotoIds = new Set<string>();
    const communityPhotos = resolveColumnPhotos(
      communityPhotosBound,
      allPhotos,
      COMMUNITY_PHOTO_KEYS,
      community,
      usedPhotoIds,
      true,
    );
    communityPhotos.forEach((photo) => usedPhotoIds.add(photoId(photo)));
    const apartmentPhotos = apartment.length > 0
      ? resolveColumnPhotos(
          apartmentPhotosBound,
          allPhotos,
          APARTMENT_PHOTO_KEYS,
          apartment,
          usedPhotoIds,
          false,
        )
      : [];

    return {
      communityItems: community,
      apartmentItems: apartment,
      communityPhotos,
      apartmentPhotos,
    };
  }, [
    property.amenities,
    property.photo_gallery,
    communityBound,
    apartmentBound,
    communityPhotosBound,
    apartmentPhotosBound,
    communityListRaw,
    apartmentListRaw,
  ]);

  useRevealStyles();

  const hasContent = communityItems.length > 0 || apartmentItems.length > 0;
  const showApartment = apartmentItems.length > 0;

  return (
    <section className="py-8 md:py-12 bg-background">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {hasContent ? (
          <div className={`amenities-grid${showApartment ? '' : ' amenities-grid--single'}`}>
            {communityItems.length > 0 && (
              <EditableCollectionBinding bindingPath="amenitieslist.communityPhotos" label="Community Photos">
                <EditableCollectionBinding bindingPath="amenitieslist.communityItems" label="Community Amenities">
                  <AmenityColumn
                    headingNode={
                      <EditableToken
                        path="amenitieslist.communityHeading"
                        as="h3"
                        className="amenities-col-heading"
                      >
                        {communityHeading || 'Community Amenities'}
                      </EditableToken>
                    }
                    headingText={communityHeading || 'Community Amenities'}
                    items={communityItems}
                    photos={communityPhotos}
                  />
                </EditableCollectionBinding>
              </EditableCollectionBinding>
            )}
            {showApartment && (
              <EditableCollectionBinding bindingPath="amenitieslist.apartmentPhotos" label="Apartment Photos">
                <EditableCollectionBinding bindingPath="amenitieslist.apartmentItems" label="Apartment Features">
                  <AmenityColumn
                    headingNode={
                      <EditableToken
                        path="amenitieslist.apartmentHeading"
                        as="h3"
                        className="amenities-col-heading"
                      >
                        {apartmentHeading || 'Apartment Features'}
                      </EditableToken>
                    }
                    headingText={apartmentHeading || 'Apartment Features'}
                    items={apartmentItems}
                    photos={apartmentPhotos}
                  />
                </EditableCollectionBinding>
              </EditableCollectionBinding>
            )}
          </div>
        ) : (
          <div className="text-center py-12 text-muted-foreground">
            <p className="font-body text-lg">Amenities will be displayed here.</p>
          </div>
        )}
      </div>

      <style>{`
        .amenities-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 2rem;
        }
        .amenities-grid--single {
          grid-template-columns: minmax(0, calc(50% - 1rem));
          justify-content: center;
        }
        @media (max-width: 768px) {
          .amenities-grid,
          .amenities-grid--single {
            grid-template-columns: 1fr;
          }
        }

        .amenities-col {
          background: white;
          border: 1px solid var(--vibe-color-border);
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }

        .amenities-slider {
          position: relative;
          width: 100%;
          height: 300px;
          overflow: hidden;
          flex-shrink: 0;
        }
        .amenities-slider-img {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          opacity: 0;
          transition: opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .amenities-slider-img--active {
          opacity: 1;
        }

        .amenities-slider-arrow {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          width: 32px;
          height: 32px;
          border-radius: 50%;
          border: none;
          background: rgba(0, 0, 0, 0.35);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          opacity: 0;
          transition: opacity 0.3s, background 0.3s;
          z-index: 2;
        }
        .amenities-slider:hover .amenities-slider-arrow {
          opacity: 1;
        }
        .amenities-slider-arrow:hover {
          background: rgba(0, 0, 0, 0.55);
        }
        .amenities-slider-arrow--prev { left: 10px; }
        .amenities-slider-arrow--next { right: 10px; }

        .amenities-slider-dots {
          position: absolute;
          bottom: 10px;
          left: 50%;
          transform: translateX(-50%);
          display: flex;
          gap: 6px;
          z-index: 2;
        }
        .amenities-slider-dot {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          border: 1.5px solid rgba(255, 255, 255, 0.7);
          background: transparent;
          padding: 0;
          cursor: pointer;
          transition: background 0.25s, border-color 0.25s, transform 0.25s;
        }
        .amenities-slider-dot:hover {
          transform: scale(1.25);
          border-color: white;
        }
        .amenities-slider-dot--active {
          background: white;
          border-color: white;
        }

        @media (prefers-reduced-motion: reduce) {
          .amenities-slider-img {
            transition: none;
          }
        }

        .amenities-col-heading {
          font-family: var(--vibe-font-heading);
          font-size: 1.35rem;
          font-weight: 400;
          color: var(--vibe-color-text);
          text-align: center;
          padding: 1.25rem 1.5rem 0;
          margin: 0;
          letter-spacing: 0.02em;
        }

        .amenities-col-list {
          list-style: none;
          padding: 1rem 1.75rem 1.5rem;
          margin: 0;
          columns: 1;
          flex: 1;
        }

        .amenities-col-item {
          display: flex;
          align-items: center;
          gap: 0.6rem;
          padding: 0.3rem 0;
          font-family: var(--vibe-font-body);
          font-size: 0.88rem;
          color: var(--vibe-color-text);
          break-inside: avoid;
          line-height: 1.5;
        }

        .amenities-col-bullet {
          flex-shrink: 0;
          width: 5px;
          height: 5px;
          border-radius: 50%;
          background: var(--vibe-color-primary);
        }

        .amenities-page-transition {
          transition: opacity 0.25s cubic-bezier(0.22, 1, 0.36, 1),
                      transform 0.25s cubic-bezier(0.22, 1, 0.36, 1);
          opacity: 1;
          transform: translateY(0);
        }
        .amenities-page-out {
          opacity: 0;
          transform: translateY(8px);
        }

        @media (prefers-reduced-motion: reduce) {
          .amenities-page-transition {
            transition: none;
          }
        }

        .amenities-pagination {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.75rem;
          padding: 0.75rem 1.5rem 1.25rem;
        }

        .amenities-page-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 30px;
          height: 30px;
          border-radius: 50%;
          border: 1px solid var(--vibe-color-border);
          background: transparent;
          color: var(--vibe-color-text-muted);
          cursor: pointer;
          transition: border-color 0.2s, color 0.2s, background 0.2s;
        }
        .amenities-page-btn:hover:not(:disabled) {
          border-color: var(--vibe-color-primary);
          color: var(--vibe-color-primary);
        }
        .amenities-page-btn:disabled {
          opacity: 0.3;
          cursor: default;
        }

        .amenities-page-dots {
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .amenities-page-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          border: 1.5px solid var(--vibe-color-border);
          background: transparent;
          padding: 0;
          cursor: pointer;
          transition: background 0.2s, border-color 0.2s, transform 0.2s;
        }
        .amenities-page-dot:hover {
          border-color: var(--vibe-color-primary);
          transform: scale(1.2);
        }
        .amenities-page-dot--active {
          background: var(--vibe-color-primary);
          border-color: var(--vibe-color-primary);
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'AmenitiesList',
  displayName: 'Amenities List',
  tokens: {
    'amenitieslist.communityHeading': {
      label: 'Community Heading',
      type: 'text',
      defaultValue: 'Community Amenities',
    },
    'amenitieslist.apartmentHeading': {
      label: 'Apartment Heading',
      type: 'text',
      defaultValue: 'Apartment Features',
    },
  },
  collectionBindings: {
    'amenitieslist.communityItems': {
      label: 'Community Amenities',
      collection: 'amenities',
    },
    'amenitieslist.apartmentItems': {
      label: 'Apartment Features',
      collection: 'amenities',
    },
    'amenitieslist.communityPhotos': {
      label: 'Community Photos',
      collection: 'photo_gallery',
    },
    'amenitieslist.apartmentPhotos': {
      label: 'Apartment Photos',
      collection: 'photo_gallery',
    },
  },
};

(AmenitiesList as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default AmenitiesList;
